// App Security's applicationCode differs by environment: QA tenants report
// SSEQACIFRAMEVONE, prod tenants report SSEPRODIFRAME. Only one can ever be
// present in a given tenant's applications list, so both are checked without
// branching on getEnvironment() (mirrors OLD_SSE_APPCODE/NEW_SSE_APPCODE in
// coreui/src/utils/app-config.ts).
const APP_SECURITY_APPCODE_QA = "SSEQACIFRAMEVONE"
const APP_SECURITY_APPCODE_PROD = "SSEPRODIFRAME"

/**
 * Fire-and-forget wrapper around the Gainsight PX global.
 *
 * No retry or polling is needed: shellui installs Gainsight's own queuing
 * stub synchronously in <head> (html/partials/gainsight-stub.html), so
 * `window.aptrinsic` exists from the first moment and buffers calls until
 * the real SDK loads, replaying them in order. That is what stops an early
 * identify() from being dropped (FPN-6545).
 *
 * The guard still matters, though: coreui is also consumed outside the
 * shell (standalone mode, other hosts), where that stub never ran and the
 * global legitimately does not exist. And analytics must never take the
 * caller down with it — these run inside load-bearing thunks — so a throw
 * from Gainsight is swallowed.
 */
function sendToAptrinsic(
  send: (aptrinsic: NonNullable<Window["aptrinsic"]>) => void
) {
  if (window.location.hostname === "localhost" || !window.aptrinsic) {
    return
  }
  try {
    send(window.aptrinsic)
  } catch (e) {
    console.warn("[analytics] Gainsight call failed:", e)
  }
}

/**
 * Tracks the ApplicationsLoaded event with per-app presence flags.
 */
export function trackApplicationsLoaded(applications: any[]) {
  sendToAptrinsic((aptrinsic) => {
    aptrinsic("track", "ApplicationsLoaded", {
      hasDLPApp: applications.some((a) => a.applicationCode === "DS"),
      hasSSE: applications.some((a) => a.applicationCode === "SSE"),
      hasWebSecurity: applications.some((a) => a.applicationCode === "WS"),
      hasRBI: applications.some((a) => a.applicationCode === "RBI"),
      hasDSPM: applications.some((a) => a.applicationCode === "DSPM"),
      hasAppSecurity: applications.some(
        (a) =>
          a.applicationCode === APP_SECURITY_APPCODE_QA ||
          a.applicationCode === APP_SECURITY_APPCODE_PROD
      ),
      hasAISEC: applications.some((a) => a.applicationCode === "AIS")
    })
  })
}

/**
 * Identifies the user in Gainsight (aptrinsic) for product analytics.
 */
export function identifyUserInAnalytics(userResponse: any) {
  sendToAptrinsic((aptrinsic) => {
    aptrinsic(
      "identify",
      {
        id: userResponse?.sub,
        email: userResponse?.userName
      },
      {
        id:
          userResponse?.ext?.tenantId?.find((a) => a?.hint === "WBSN")?.id ||
          "unknown",
        WBSNAlternateID:
          userResponse?.ext?.tenantId?.find((a) => a?.hint === "WBSN")?.id ||
          "unknown"
      }
    )
  })
}

/**
 * Clears Gainsight's cached visitor identity so the next login starts a
 * fresh PX session.
 *
 * FPN-6545: nothing ever cleared PX's identity on logout, so PX kept the
 * last-identified visitor for the lifetime of the browser profile. A
 * second user logging in on the same browser inherited the previous
 * user's identity — the ticket's "Platform persists Gainsight user
 * session across multiple account log ins" and the Gainsight team's
 * "keeps using the cached/stale data that's not being cleared".
 *
 * Note this one call must reach Gainsight *synchronously*: appLogout hands
 * off to clearStorageData(true), which navigates the document away
 * immediately afterwards. sendToAptrinsic is synchronous (the queuing stub
 * removed any need to defer or retry), so that holds — but never route
 * this through anything that defers.
 */
export function resetAnalyticsIdentity() {
  sendToAptrinsic((aptrinsic) => aptrinsic("reset"))
}
